import { ReactNode } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

// Page layout primitives, ported from the desktop app's PageLayout so both apps
// share one look: a left-aligned column, a 20px/700 title with a grey subtitle
// OUTSIDE the cards, and a 16px/600 section heading INSIDE each card. They
// replace the two-column SettingBox/LeftSide layout (CONTRACT #13).

/** Settings-type pages. */
export const PAGE_MAX_WIDTH = 640;

const PAGE_CARD_SX = {
  // The desktop app has no phone widths; tighten the card there like the old
  // WhiteWrapper did so tables keep their room.
  p: { xs: 2, md: 3 },
  border: '1px solid',
  borderColor: 'neutral.otherBorder',
  borderRadius: '15px',
  boxShadow: 'none',
} as const;

const SECTION_TITLE_SX = {
  fontWeight: 600,
  color: 'grey.900',
  fontSize: '16px',
  mb: 2,
} as const;

interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right-aligned control beside the title (e.g. a Refresh button). */
  action?: ReactNode;
  sx?: SxProps<Theme>;
}

export function PageHeader({ title, subtitle, action, sx }: PageHeaderProps) {
  return (
    <Box
      sx={[
        { mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box>
        <Typography variant="h6" component="h1" sx={{ fontWeight: 700, color: 'grey.900' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: 'neutral.greyFont', mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
  );
}

interface PageSectionProps {
  title?: ReactNode;
  children: ReactNode;
  sx?: SxProps<Theme>;
}

export function PageSection({ title, children, sx }: PageSectionProps) {
  return (
    <Paper sx={[PAGE_CARD_SX, { mb: 3 }, ...(Array.isArray(sx) ? sx : [sx])]}>
      {title && (
        <Typography variant="h6" component="h2" sx={SECTION_TITLE_SX}>
          {title}
        </Typography>
      )}
      {children}
    </Paper>
  );
}
